import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Card, Badge, ProgressBar } from 'react-bootstrap';
import { FaShieldAlt, FaServer, FaCheckCircle, FaBuilding, FaCodeBranch, FaAward, FaExternalLinkAlt } from 'react-icons/fa';
import apiService from '../../../../services/apiServices';

const AboutModal = ({ show, onHide }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!show) return;

    const fetchInfo = async () => {
      try {
        const res = await apiService.get('/system/info');
        setInfo(res.data);
      } catch (err) {
        console.error('Failed to load system info', err);
      }
    };

    fetchInfo();
  }, [show]);

  const remainingDays = info?.remainingDays ?? 0;
  const maxDays = 730; // standard 2-year license lifecycle
  const licenseProgress = Math.min(100, Math.max(5, (remainingDays / maxDays) * 100));

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      contentClassName="border-0 shadow-lg"
      style={{ borderRadius: '20px', overflow: 'hidden' }}
    >
      {/* HEADER */}
      <div 
        className="p-4 text-white position-relative overflow-hidden" 
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}
      >
        <div 
          style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '180px',
            height: '180px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.25) 0%, rgba(255,255,255,0) 70%)',
            filter: 'blur(30px)'
          }} 
        />
        
        <div className="d-flex align-items-center justify-content-between position-relative" style={{ zIndex: 1 }}>
          <div className="d-flex align-items-center gap-3">
            <div 
              className="rounded-3 d-flex align-items-center justify-content-center shadow-sm"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                color: '#fff',
                fontSize: '1.25rem',
                fontWeight: 900
              }}
            >
              iS
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold text-white" style={{ letterSpacing: '-0.3px' }}>iShopMaster PRO</h5>
                <Badge bg="primary" className="px-2 py-0.5 rounded-pill" style={{ fontSize: '0.65rem' }}>Enterprise</Badge>
              </div>
              <small className="text-white text-opacity-75" style={{ fontSize: '0.78rem' }}>
                Next-Gen Mobile Retail & Diagnostics Engine
              </small>
            </div>
          </div>
          <button 
            type="button" 
            className="btn-close btn-close-white" 
            aria-label="Close" 
            onClick={onHide}
          />
        </div>
      </div>

      {/* BODY */}
      <Modal.Body className="p-4 bg-light bg-opacity-50">
        
        {/* LICENSE TELEMETRY CARD */}
        <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px', background: '#ffffff' }}>
          <Card.Body className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="text-uppercase fw-bold text-muted" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
                Enterprise License Status
              </span>
              <span className="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-1 rounded-pill" style={{ fontSize: '0.7rem' }}>
                <FaCheckCircle className="me-1" /> Active Node
              </span>
            </div>

            <div className="d-flex align-items-baseline gap-2 mb-2">
              <h3 className="fw-900 text-dark mb-0">{remainingDays}</h3>
              <span className="text-muted small fw-semibold">Days Remaining</span>
            </div>

            <ProgressBar 
              now={licenseProgress} 
              variant={remainingDays > 60 ? 'primary' : 'warning'} 
              style={{ height: '6px', borderRadius: '4px' }} 
              className="mb-3"
            />

            <Row className="g-2 pt-2 border-top">
              <Col xs={6}>
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Node Version</div>
                <div className="fw-bold text-dark small">{info?.version || '1.7.1'}</div>
              </Col>
              <Col xs={6}>
                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Access Tier</div>
                <div className="fw-bold text-dark small">{info?.accessType || 'Dedicated Tenant'}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ACKNOWLEDGEMENTS & ENGINEERING */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '14px', background: '#ffffff' }}>
          <Card.Body className="p-3">
            <div className="text-uppercase fw-bold text-muted mb-2" style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}>
              Architecture & Operational Partners
            </div>

            {info?.acknowledgements && info.acknowledgements.length > 0 ? (
              info.acknowledgements.map((ack, idx) => (
                <div key={idx} className="d-flex align-items-center gap-3 p-2 rounded-3 bg-light mb-2">
                  {ack.logo && (
                    <img
                      src={ack.logo}
                      alt={ack.name}
                      style={{ width: 36, height: 36, objectFit: 'contain' }}
                      className="rounded"
                    />
                  )}
                  <div>
                    <div className="fw-bold text-dark small">{ack.name}</div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>{ack.role}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="d-flex align-items-center gap-2.5 p-2.5 rounded-3 bg-light">
                <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-circle">
                  <FaBuilding size={16} />
                </div>
                <div>
                  <div className="fw-bold text-dark small">Pettah GSM Core Network</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>Operational Workflow & Diagnostics Architecture</div>
                </div>
              </div>
            )}

            <div className="mt-3 pt-2 border-top text-center">
              <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                Engineered with high-reliability standards by{' '}
                <span className="fw-bold text-dark">JJSOFT GLOBAL</span>
              </span>
            </div>
          </Card.Body>
        </Card>

      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer className="border-0 bg-white py-3 px-4 d-flex justify-content-between">
        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
          © {new Date().getFullYear()} JJSOFT GLOBAL
        </small>
        <Button 
          variant="dark" 
          size="sm" 
          onClick={onHide} 
          className="rounded-pill px-4 fw-semibold"
          style={{ fontSize: '0.78rem' }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AboutModal;
